import { BuilderCampaignType } from 'workflows/CampaignCreateWorkflow'

import { MasterCampaign, MasterCampaignCampaignItem } from 'api/interfaces/MasterCampaign'
import { MasterCampaignPlan } from 'api/interfaces/MasterCampaignPlan'

import { Campaign } from '../../interfaces'
import { defaultTransformFn, platformApi } from './platformApi'

const masterCampaignsTags = platformApi.enhanceEndpoints({ addTagTypes: ['MasterCampaigns', 'MasterCampaign', 'MasterCampaignCampaigns'] })

export const masterCampaignsApi = masterCampaignsTags.injectEndpoints({
  endpoints: build => ({
    getMasterCampaigns: build.query<MasterCampaign[], { accountId: string }>({
      query: ({ accountId }) => ({
        url: `masterCampaigns`,
        method: 'GET',
        params: {
          accountId
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaigns', id: request.accountId }]
    }),
    getMasterCampaign: build.query<MasterCampaign, { masterCampaignId: string }>({
      query: ({ masterCampaignId }) => ({
        url: `masterCampaigns/${masterCampaignId}`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaign', id: request.masterCampaignId }]
    }),
    getMasterCampaignCampaigns: build.query<Campaign[], { masterCampaignId: string }>({
      query: ({ masterCampaignId }) => ({
        url: `masterCampaigns/${masterCampaignId}/campaigns`,
        method: 'GET',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      providesTags: (_result, _error, request) => [{ type: 'MasterCampaignCampaigns', id: request.masterCampaignId }]
    }),
    addCampaignToMasterCampaign: build.mutation<MasterCampaignPlan, { masterCampaignId: string; campaignId: string }>({
      query: ({ masterCampaignId, campaignId }) => ({
        url: `masterCampaigns/${masterCampaignId}/campaigns`,
        method: 'POST',
        data: {
          campaignId
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [{ type: 'MasterCampaignCampaigns', id: request.masterCampaignId }]
    }),
    deleteMasterCampaignCampaign: build.mutation<MasterCampaignPlan, { accountId: string; masterCampaignId: string; campaignId: string }>({
      query: ({ accountId, campaignId }) => ({
        url: `accounts/${accountId}/campaigns/${campaignId}`,
        method: 'DELETE',
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (result, _error, request) => [{ type: 'MasterCampaignCampaigns', id: request.masterCampaignId }]
    }),
    updateMasterCampaign: build.mutation<
      MasterCampaign,
      {
        masterCampaignId: string
        data: {
          name?: string
          paymentMethodId?: string
          paymentMethodSelectedDate?: string
          activateDaysBeforeStartDate?: number
          updatedBy: string
          masterCampaignItems: MasterCampaignCampaignItem[]
        }
      }
    >({
      query: ({ masterCampaignId, data: { name, paymentMethodId, activateDaysBeforeStartDate, updatedBy, masterCampaignItems, paymentMethodSelectedDate } }) => ({
        url: `masterCampaigns/${masterCampaignId}`,
        method: 'PUT',
        data: {
          name,
          paymentMethodId,
          activateDaysBeforeStartDate,
          paymentMethodSelectedDate,
          updatedBy,
          masterCampaignItems
        },
        isContext: true,
        isAgency: false
      }),
      onQueryStarted({ masterCampaignId, data }, { dispatch, queryFulfilled }): Promise<void> | void {
        dispatch(
          masterCampaignsApi.util.updateQueryData('getMasterCampaign', { masterCampaignId }, (draft: MasterCampaign) => {
            draft.items = data.masterCampaignItems
          })
        )

        return queryFulfilled.then()
      },
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, { masterCampaignId }) => [{ type: 'MasterCampaign', id: masterCampaignId }]
    }),
    updateMasterCampaignCampaignItem: build.mutation<
      MasterCampaign,
      {
        masterCampaignId: string
        data: {
          campaignId: string
          paymentMethodId?: string
          activateDaysBeforeStartDate?: number
          autoActivate: boolean
        }
      }
    >({
      query: ({ masterCampaignId, data: { campaignId, paymentMethodId, activateDaysBeforeStartDate, autoActivate } }) => ({
        url: `masterCampaigns/${masterCampaignId}/updateMasterCampaignCampaignItem`,
        method: 'POST',
        data: {
          campaignId,
          paymentMethodId,
          activateDaysBeforeStartDate,
          autoActivate
        },
        isContext: true,
        isAgency: false
      }),
      transformResponse: defaultTransformFn,
      invalidatesTags: (_result, _error, { masterCampaignId }) => [{ type: 'MasterCampaign', id: masterCampaignId }]
    })
  })
})

export const { useGetMasterCampaignsQuery, useGetMasterCampaignQuery, useGetMasterCampaignCampaignsQuery, useDeleteMasterCampaignCampaignMutation, useUpdateMasterCampaignMutation, useUpdateMasterCampaignCampaignItemMutation, useAddCampaignToMasterCampaignMutation } = masterCampaignsApi
